// ============================================
// Screen Space Lens Flare Post-Process Effect
// ============================================
// Production-quality lens flare with:
// - Karis average threshold (anti-firefly)
// - Multi-scale ghosts with iris aperture shapes
// - Halo ring simulation
// - Multi-directional starburst streaks
// - Lens dirt integration
// - Multiple blend modes

import type { IRenderContext } from '../../core/IRenderContext';
import { OffscreenTarget } from '../../core/RenderPass';
import { 
  PostProcessEffect, 
  type PostProcessContext, 
  type PostProcessParameter 
} from '../PostProcessStack';
import {
  LENS_FLARE_THRESHOLD_LAYOUT,
  LENS_FLARE_FLARES_LAYOUT,
  LENS_FLARE_STREAKS_LAYOUT,
  LENS_FLARE_COMPOSITE_LAYOUT,
} from '../PostProcessLayouts';
import {
  type PipelineHandle,
  type ShaderHandle,
  NULL_HANDLE,
} from '../../types';
import { getCompiledShader } from '../ShaderCache';

/**
 * Screen Space Lens Flare post-processing effect
 * 
 * Simulates camera lens flare artifacts (ghosts, halos, streaks)
 * arising from bright light sources in the scene.
 * 
 * Multi-pass effect:
 * 1. Threshold extraction (Karis average)
 * 2. Ghosts/Flares generation (with iris shapes)
 * 3. Streaks (multi-directional starburst)
 * 4. Composite with original (blend modes, lens dirt)
 */
export class LensFlareEffect extends PostProcessEffect {
  readonly type = 'lensFlare';
  readonly displayName = 'Lens Flare';
  
  // === Threshold parameters ===
  threshold = 1.0;          // Brightness threshold
  softKnee = 0.5;           // Soft transition
  thresholdAnamorphic = 0;  // Pre-stretch for threshold
  
  // === Ghosts/Flares parameters ===
  flaresIntensity = 1.0;
  ghostCount = 5;
  ghostSpacing = 0.2;
  ghostScale = 1.0;
  chromaticAmount = 1.0;
  
  // Halo
  haloRadius = 0.5;
  haloWidth = 0.2;
  haloIntensity = 0.3;
  
  // Anamorphic & Shape
  anamorphicX = 1.0;
  anamorphicY = 1.0;
  irisShape = 0;            // 0=circle, 6=hexagon, 8=octagon
  irisRotation = 0;
  
  // Tint
  tintR = 1.0;
  tintG = 0.9;
  tintB = 0.7;
  vignetteEffect = 0.8;
  
  // === Streaks parameters ===
  streaksEnabled = true;
  streaksIntensity = 0.5;
  streaksLength = 1.0;
  streaksThreshold = 0.5;
  streaksSamples = 16;
  streaksRayCount = 4;       // 2=horizontal, 4=cross, 6=star, 8=sun
  streaksRotation = 0;
  streaksAnamorphicX = 1.5;  // Usually stretched horizontally
  streaksAnamorphicY = 1.0;
  streaksFalloff = 2.0;
  streaksChromatic = 0.5;
  
  // === Composite parameters ===
  overallIntensity = 1.0;
  flaresContribution = 1.0;
  streaksContribution = 1.0;
  blendMode = 0;             // 0=additive, 1=screen, 2=soft light
  saturation = 1.0;
  temperature = 0;           // -1 cool, +1 warm
  dirtIntensity = 0;         // Lens dirt
  
  // GPU resources
  private thresholdShader: ShaderHandle = NULL_HANDLE;
  private flaresShader: ShaderHandle = NULL_HANDLE;
  private streaksShader: ShaderHandle = NULL_HANDLE;
  private compositeShader: ShaderHandle = NULL_HANDLE;
  
  private thresholdPipeline: PipelineHandle = NULL_HANDLE;
  private flaresPipeline: PipelineHandle = NULL_HANDLE;
  private streaksPipeline: PipelineHandle = NULL_HANDLE;
  private compositePipelineHDR: PipelineHandle = NULL_HANDLE;
  private compositePipelineLDR: PipelineHandle = NULL_HANDLE;
  
  // Render targets
  private brightTarget: OffscreenTarget | null = null;
  private flaresTarget: OffscreenTarget | null = null;
  private streaksTarget: OffscreenTarget | null = null;
  
  // Uniform writers
  private thresholdWriter = LENS_FLARE_THRESHOLD_LAYOUT.createWriter();
  private flaresWriter = LENS_FLARE_FLARES_LAYOUT.createWriter();
  private streaksWriter = LENS_FLARE_STREAKS_LAYOUT.createWriter();
  private compositeWriter = LENS_FLARE_COMPOSITE_LAYOUT.createWriter();
  
  getParameters(): PostProcessParameter[] {
    return [
      // Threshold
      { name: 'threshold', type: 'float', min: 0, max: 5, default: 1.0, description: 'Brightness threshold' },
      { name: 'softKnee', type: 'float', min: 0, max: 1, default: 0.5, description: 'Soft knee size' },
      { name: 'thresholdAnamorphic', type: 'float', min: -1, max: 1, default: 0, description: 'Pre-stretch' },
      
      // Ghosts/Flares
      { name: 'flaresIntensity', type: 'float', min: 0, max: 5, default: 1.0, description: 'Ghosts intensity' },
      { name: 'ghostCount', type: 'int', min: 1, max: 12, default: 5, description: 'Number of ghosts' },
      { name: 'ghostSpacing', type: 'float', min: 0.05, max: 0.5, default: 0.2, description: 'Ghost spacing' },
      { name: 'ghostScale', type: 'float', min: 0.1, max: 3, default: 1.0, description: 'Ghost scale' },
      { name: 'chromaticAmount', type: 'float', min: 0, max: 5, default: 1.0, description: 'Chromatic aberration' },
      
      // Halo
      { name: 'haloRadius', type: 'float', min: 0.1, max: 0.9, default: 0.5, description: 'Halo ring radius' },
      { name: 'haloWidth', type: 'float', min: 0.05, max: 0.5, default: 0.2, description: 'Halo ring width' },
      { name: 'haloIntensity', type: 'float', min: 0, max: 2, default: 0.3, description: 'Halo brightness' },
      
      // Anamorphic & Shape
      { name: 'anamorphicX', type: 'float', min: 0.2, max: 3, default: 1.0, description: 'Horizontal stretch' },
      { name: 'anamorphicY', type: 'float', min: 0.2, max: 3, default: 1.0, description: 'Vertical stretch' },
      { name: 'irisShape', type: 'float', min: 0, max: 8, default: 0, description: 'Iris shape (0=circle, 6=hex)' },
      { name: 'irisRotation', type: 'float', min: 0, max: 6.28, default: 0, description: 'Iris rotation' },
      
      // Tint
      { name: 'tintR', type: 'float', min: 0, max: 2, default: 1.0, description: 'Tint red' },
      { name: 'tintG', type: 'float', min: 0, max: 2, default: 0.9, description: 'Tint green' },
      { name: 'tintB', type: 'float', min: 0, max: 2, default: 0.7, description: 'Tint blue' },
      { name: 'vignetteEffect', type: 'float', min: 0, max: 2, default: 0.8, description: 'Edge vignette' },
      
      // Streaks
      { name: 'streaksEnabled', type: 'bool', default: true, description: 'Enable streaks' },
      { name: 'streaksIntensity', type: 'float', min: 0, max: 3, default: 0.5, description: 'Streaks intensity' },
      { name: 'streaksLength', type: 'float', min: 0.1, max: 3, default: 1.0, description: 'Streaks length' },
      { name: 'streaksThreshold', type: 'float', min: 0, max: 2, default: 0.5, description: 'Streaks threshold' },
      { name: 'streaksSamples', type: 'int', min: 4, max: 32, default: 16, description: 'Streaks samples' },
      { name: 'streaksRayCount', type: 'int', min: 2, max: 8, default: 4, description: 'Ray count (2/4/6/8)' },
      { name: 'streaksRotation', type: 'float', min: 0, max: 6.28, default: 0, description: 'Streaks rotation' },
      { name: 'streaksAnamorphicX', type: 'float', min: 0.2, max: 3, default: 1.5, description: 'Streaks H stretch' },
      { name: 'streaksAnamorphicY', type: 'float', min: 0.2, max: 3, default: 1.0, description: 'Streaks V stretch' },
      { name: 'streaksFalloff', type: 'float', min: 0.5, max: 4, default: 2.0, description: 'Streaks falloff' },
      { name: 'streaksChromatic', type: 'float', min: 0, max: 2, default: 0.5, description: 'Streaks chromatic' },
      
      // Composite
      { name: 'overallIntensity', type: 'float', min: 0, max: 3, default: 1.0, description: 'Overall intensity' },
      { name: 'flaresContribution', type: 'float', min: 0, max: 2, default: 1.0, description: 'Ghosts contribution' },
      { name: 'streaksContribution', type: 'float', min: 0, max: 2, default: 1.0, description: 'Streaks contribution' },
      { name: 'blendMode', type: 'int', min: 0, max: 2, default: 0, description: 'Blend mode' },
      { name: 'saturation', type: 'float', min: 0, max: 2, default: 1.0, description: 'Flare saturation' },
      { name: 'temperature', type: 'float', min: -1, max: 1, default: 0, description: 'Color temperature' },
      { name: 'dirtIntensity', type: 'float', min: 0, max: 2, default: 0, description: 'Lens dirt' },
    ];
  }
  
  getParameterValue(name: string): number | boolean | number[] {
    switch (name) {
      // Threshold
      case 'threshold': return this.threshold;
      case 'softKnee': return this.softKnee;
      case 'thresholdAnamorphic': return this.thresholdAnamorphic;
      
      // Ghosts
      case 'flaresIntensity': return this.flaresIntensity;
      case 'ghostCount': return this.ghostCount;
      case 'ghostSpacing': return this.ghostSpacing;
      case 'ghostScale': return this.ghostScale;
      case 'chromaticAmount': return this.chromaticAmount;
      
      // Halo
      case 'haloRadius': return this.haloRadius;
      case 'haloWidth': return this.haloWidth;
      case 'haloIntensity': return this.haloIntensity;
      
      // Anamorphic & Shape
      case 'anamorphicX': return this.anamorphicX;
      case 'anamorphicY': return this.anamorphicY;
      case 'irisShape': return this.irisShape;
      case 'irisRotation': return this.irisRotation;
      
      // Tint
      case 'tintR': return this.tintR;
      case 'tintG': return this.tintG;
      case 'tintB': return this.tintB;
      case 'vignetteEffect': return this.vignetteEffect;
      
      // Streaks  
      case 'streaksEnabled': return this.streaksEnabled;
      case 'streaksIntensity': return this.streaksIntensity;
      case 'streaksLength': return this.streaksLength;
      case 'streaksThreshold': return this.streaksThreshold;
      case 'streaksSamples': return this.streaksSamples;
      case 'streaksRayCount': return this.streaksRayCount;
      case 'streaksRotation': return this.streaksRotation;
      case 'streaksAnamorphicX': return this.streaksAnamorphicX;
      case 'streaksAnamorphicY': return this.streaksAnamorphicY;
      case 'streaksFalloff': return this.streaksFalloff;
      case 'streaksChromatic': return this.streaksChromatic;
      
      // Composite
      case 'overallIntensity': return this.overallIntensity;
      case 'flaresContribution': return this.flaresContribution;
      case 'streaksContribution': return this.streaksContribution;
      case 'blendMode': return this.blendMode;
      case 'saturation': return this.saturation;
      case 'temperature': return this.temperature;
      case 'dirtIntensity': return this.dirtIntensity;
      
      default: return 0;
    }
  }
  
  setParameterValue(name: string, value: number | boolean | number[]): void {
    switch (name) {
      // Threshold
      case 'threshold': if (typeof value === 'number') this.threshold = value; break;
      case 'softKnee': if (typeof value === 'number') this.softKnee = value; break;
      case 'thresholdAnamorphic': if (typeof value === 'number') this.thresholdAnamorphic = value; break;
      
      // Ghosts
      case 'flaresIntensity': if (typeof value === 'number') this.flaresIntensity = value; break;
      case 'ghostCount': if (typeof value === 'number') this.ghostCount = Math.floor(value); break;
      case 'ghostSpacing': if (typeof value === 'number') this.ghostSpacing = value; break;
      case 'ghostScale': if (typeof value === 'number') this.ghostScale = value; break;
      case 'chromaticAmount': if (typeof value === 'number') this.chromaticAmount = value; break;
      
      // Halo
      case 'haloRadius': if (typeof value === 'number') this.haloRadius = value; break;
      case 'haloWidth': if (typeof value === 'number') this.haloWidth = value; break;
      case 'haloIntensity': if (typeof value === 'number') this.haloIntensity = value; break;
      
      // Anamorphic & Shape
      case 'anamorphicX': if (typeof value === 'number') this.anamorphicX = value; break;
      case 'anamorphicY': if (typeof value === 'number') this.anamorphicY = value; break;
      case 'irisShape': if (typeof value === 'number') this.irisShape = value; break;
      case 'irisRotation': if (typeof value === 'number') this.irisRotation = value; break;
      
      // Tint
      case 'tintR': if (typeof value === 'number') this.tintR = value; break;
      case 'tintG': if (typeof value === 'number') this.tintG = value; break;
      case 'tintB': if (typeof value === 'number') this.tintB = value; break;
      case 'vignetteEffect': if (typeof value === 'number') this.vignetteEffect = value; break;
      
      // Streaks
      case 'streaksEnabled': if (typeof value === 'boolean') this.streaksEnabled = value; break;
      case 'streaksIntensity': if (typeof value === 'number') this.streaksIntensity = value; break;
      case 'streaksLength': if (typeof value === 'number') this.streaksLength = value; break;
      case 'streaksThreshold': if (typeof value === 'number') this.streaksThreshold = value; break;
      case 'streaksSamples': if (typeof value === 'number') this.streaksSamples = Math.floor(value); break;
      case 'streaksRayCount': if (typeof value === 'number') this.streaksRayCount = Math.floor(value); break;
      case 'streaksRotation': if (typeof value === 'number') this.streaksRotation = value; break;
      case 'streaksAnamorphicX': if (typeof value === 'number') this.streaksAnamorphicX = value; break;
      case 'streaksAnamorphicY': if (typeof value === 'number') this.streaksAnamorphicY = value; break;
      case 'streaksFalloff': if (typeof value === 'number') this.streaksFalloff = value; break;
      case 'streaksChromatic': if (typeof value === 'number') this.streaksChromatic = value; break;
      
      // Composite
      case 'overallIntensity': if (typeof value === 'number') this.overallIntensity = value; break;
      case 'flaresContribution': if (typeof value === 'number') this.flaresContribution = value; break;
      case 'streaksContribution': if (typeof value === 'number') this.streaksContribution = value; break;
      case 'blendMode': if (typeof value === 'number') this.blendMode = Math.floor(value); break;
      case 'saturation': if (typeof value === 'number') this.saturation = value; break;
      case 'temperature': if (typeof value === 'number') this.temperature = value; break;
      case 'dirtIntensity': if (typeof value === 'number') this.dirtIntensity = value; break;
    }
  }
  
  protected onInitialize(ctx: IRenderContext): void {
    const hdrFormat = 'rgba16float' as const;
    const ldrFormat = ctx.getSwapChainFormat();
    
    // Create shaders
    const thresholdWgsl = getCompiledShader('postprocess/lens-flare-threshold');
    const flaresWgsl = getCompiledShader('postprocess/lens-flare-flares');
    const streaksWgsl = getCompiledShader('postprocess/lens-flare-streaks');
    const compositeWgsl = getCompiledShader('postprocess/lens-flare-composite');
    this.thresholdShader = ctx.createShader(thresholdWgsl, thresholdWgsl);
    this.flaresShader = ctx.createShader(flaresWgsl, flaresWgsl);
    this.streaksShader = ctx.createShader(streaksWgsl, streaksWgsl);
    this.compositeShader = ctx.createShader(compositeWgsl, compositeWgsl);
    
    // Create pipelines
    this.thresholdPipeline = ctx.createPipeline({
      shader: this.thresholdShader,
      vertexBuffers: [],
      colorTargets: [{ format: hdrFormat }],
      primitive: { topology: 'triangle-list', cullMode: 'none', usesTextures: true, textureCount: 1 },
      label: 'lens-flare-threshold',
    });
    
    this.flaresPipeline = ctx.createPipeline({
      shader: this.flaresShader,
      vertexBuffers: [],
      colorTargets: [{ format: hdrFormat }],
      primitive: { topology: 'triangle-list', cullMode: 'none', usesTextures: true, textureCount: 1 },
      label: 'lens-flare-flares',
    });
    
    this.streaksPipeline = ctx.createPipeline({
      shader: this.streaksShader,
      vertexBuffers: [],
      colorTargets: [{ format: hdrFormat }],
      primitive: { topology: 'triangle-list', cullMode: 'none', usesTextures: true, textureCount: 1 },
      label: 'lens-flare-streaks',
    });
    
    this.compositePipelineHDR = ctx.createPipeline({
      shader: this.compositeShader,
      vertexBuffers: [],
      colorTargets: [{ format: hdrFormat }],
      primitive: { topology: 'triangle-list', cullMode: 'none', usesTextures: true, textureCount: 3 },
      label: 'lens-flare-composite-hdr',
    });
    
    this.compositePipelineLDR = ctx.createPipeline({
      shader: this.compositeShader,
      vertexBuffers: [],
      colorTargets: [{ format: ldrFormat }],
      primitive: { topology: 'triangle-list', cullMode: 'none', usesTextures: true, textureCount: 3 },
      label: 'lens-flare-composite-ldr',
    });
  }
  
  protected onResize(ctx: IRenderContext, width: number, height: number): void {
    // Destroy old targets
    this.brightTarget?.destroy();
    this.flaresTarget?.destroy();
    this.streaksTarget?.destroy();
    
    // Create targets at half resolution for performance
    const halfW = Math.max(1, Math.floor(width / 2));
    const halfH = Math.max(1, Math.floor(height / 2));
    
    this.brightTarget = new OffscreenTarget(ctx, halfW, halfH, false, true);
    this.flaresTarget = new OffscreenTarget(ctx, halfW, halfH, false, true);
    this.streaksTarget = new OffscreenTarget(ctx, halfW, halfH, false, true);
  }
  
  protected onRender(context: PostProcessContext): void {
    const { ctx, source, destination, width, height, intensity, isHdrTarget } = context;
    
    // Ensure we have targets
    if (!this.brightTarget || !this.flaresTarget || !this.streaksTarget) {
      this.onResize(ctx, width, height);
    }
    
    if (!this.brightTarget || !this.flaresTarget || !this.streaksTarget) {
      return;
    }
    
    const halfW = this.brightTarget.width;
    const halfH = this.brightTarget.height;

    // Every pass renders via fullscreenPass() so its 'packed' uniforms are snapshotted per draw —
    // all four passes land in one queue submit, and without snapshots they'd all read the
    // composite pass's uniform values.

    // === Pass 1: Threshold extraction ===
    ctx.setRenderTarget(this.brightTarget.handle);
    ctx.clear(0, 0, 0, 1);
    ctx.setViewport(0, 0, halfW, halfH);

    this.thresholdWriter.setAll({
      threshold: this.threshold,
      softKnee: this.softKnee,
      anamorphic: this.thresholdAnamorphic,
      _pad: 0,
    });
    this.fullscreenPass(ctx, this.thresholdPipeline, this.thresholdWriter.float32, [source]);

    // === Pass 2: Ghosts/Flares generation ===
    ctx.setRenderTarget(this.flaresTarget.handle);
    ctx.clear(0, 0, 0, 1);
    ctx.setViewport(0, 0, halfW, halfH);

    this.flaresWriter.setAll({
      intensity: this.flaresIntensity,
      ghostCount: this.ghostCount,
      ghostSpacing: this.ghostSpacing,
      ghostScale: this.ghostScale,
      chromaticAmount: this.chromaticAmount,
      haloRadius: this.haloRadius,
      haloWidth: this.haloWidth,
      haloIntensity: this.haloIntensity,
      anamorphicX: this.anamorphicX,
      anamorphicY: this.anamorphicY,
      irisShape: this.irisShape,
      irisRotation: this.irisRotation,
      tintColor: [this.tintR, this.tintG, this.tintB],
      vignetteEffect: this.vignetteEffect,
    });
    this.fullscreenPass(ctx, this.flaresPipeline, this.flaresWriter.float32, [
      this.brightTarget.getTexture(),
    ]);

    // === Pass 3: Streaks (multi-directional starburst) ===
    if (this.streaksEnabled && this.streaksContribution > 0) {
      ctx.setRenderTarget(this.streaksTarget.handle);
      ctx.clear(0, 0, 0, 1);
      ctx.setViewport(0, 0, halfW, halfH);

      this.streaksWriter.setAll({
        intensity: this.streaksIntensity,
        length: this.streaksLength,
        threshold: this.streaksThreshold,
        samples: this.streaksSamples,
        rayCount: this.streaksRayCount,
        rotation: this.streaksRotation,
        anamorphicX: this.streaksAnamorphicX,
        anamorphicY: this.streaksAnamorphicY,
        falloffExponent: this.streaksFalloff,
        chromatic: this.streaksChromatic,
        _pad0: 0,
        _pad1: 0,
      });
      this.fullscreenPass(ctx, this.streaksPipeline, this.streaksWriter.float32, [
        this.brightTarget.getTexture(),
      ]);
    }

    // === Pass 4: Composite ===
    ctx.setRenderTarget(destination);
    ctx.clear(0, 0, 0, 1);
    ctx.setViewport(0, 0, width, height);

    const compositePipeline = isHdrTarget ? this.compositePipelineHDR : this.compositePipelineLDR;

    this.compositeWriter.setAll({
      intensity: this.overallIntensity * intensity,
      flaresContribution: this.flaresContribution,
      streaksContribution: this.streaksEnabled ? this.streaksContribution : 0,
      blendMode: this.blendMode,
      saturation: this.saturation,
      temperature: this.temperature,
      dirtIntensity: this.dirtIntensity,
      _pad: 0,
    });
    this.fullscreenPass(ctx, compositePipeline, this.compositeWriter.float32, [
      source,
      this.flaresTarget.getTexture(),
      this.streaksTarget.getTexture(),
    ]);
  }
  
  protected onDispose(ctx: IRenderContext): void {
    // Destroy targets
    this.brightTarget?.destroy();
    this.flaresTarget?.destroy();
    this.streaksTarget?.destroy();
    this.brightTarget = null;
    this.flaresTarget = null;
    this.streaksTarget = null;
    
    // Destroy pipelines
    if (this.thresholdPipeline !== NULL_HANDLE) ctx.destroyPipeline(this.thresholdPipeline);
    if (this.flaresPipeline !== NULL_HANDLE) ctx.destroyPipeline(this.flaresPipeline);
    if (this.streaksPipeline !== NULL_HANDLE) ctx.destroyPipeline(this.streaksPipeline);
    if (this.compositePipelineHDR !== NULL_HANDLE) ctx.destroyPipeline(this.compositePipelineHDR);
    if (this.compositePipelineLDR !== NULL_HANDLE) ctx.destroyPipeline(this.compositePipelineLDR);
    
    // Destroy shaders
    if (this.thresholdShader !== NULL_HANDLE) ctx.destroyShader(this.thresholdShader);
    if (this.flaresShader !== NULL_HANDLE) ctx.destroyShader(this.flaresShader);
    if (this.streaksShader !== NULL_HANDLE) ctx.destroyShader(this.streaksShader);
    if (this.compositeShader !== NULL_HANDLE) ctx.destroyShader(this.compositeShader);
    
    this.thresholdPipeline = NULL_HANDLE;
    this.flaresPipeline = NULL_HANDLE;
    this.streaksPipeline = NULL_HANDLE;
    this.compositePipelineHDR = NULL_HANDLE;
    this.compositePipelineLDR = NULL_HANDLE;
    this.thresholdShader = NULL_HANDLE;
    this.flaresShader = NULL_HANDLE;
    this.streaksShader = NULL_HANDLE;
    this.compositeShader = NULL_HANDLE;
  }
}
